import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useBedarfeList } from '../../api/bedarfe';
import { useCreateKennenlernen } from '../../api/kennenlernen';
import { BedarfStatus, type Bedarf } from '../../types/bedarf';

interface SelectBedarfKennenlernenModalProps {
  isOpen: boolean;
  onClose: () => void;
  bewerberId: string;
  bewerberName: string;
  onSuccess?: () => void;
}

interface FormData {
  bedarfId: string;
  scheduledAt: string;
  location: string;
  notes: string;
}

export function SelectBedarfKennenlernenModal({
  isOpen,
  onClose,
  bewerberId,
  bewerberName,
  onSuccess,
}: SelectBedarfKennenlernenModalProps) {
  const createMutation = useCreateKennenlernen();
  const { data: bedarfeData, isLoading: isLoadingBedarfe } = useBedarfeList({ limit: 200 });
  const [error, setError] = useState<string | null>(null);
  const [selectedBedarf, setSelectedBedarf] = useState<Bedarf | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      bedarfId: '',
      scheduledAt: '',
      location: '',
      notes: '',
    },
  });

  const watchBedarfId = watch('bedarfId');

  // Update selected Bedarf when dropdown changes
  const handleBedarfChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const bedarfId = e.target.value;
    if (bedarfId && bedarfeData?.data) {
      const bedarf = bedarfeData.data.find(b => b.id === bedarfId);
      setSelectedBedarf(bedarf || null);
    } else {
      setSelectedBedarf(null);
    }
  };

  const handleFormSubmit = async (data: FormData) => {
    setError(null);
    try {
      await createMutation.mutateAsync({
        bedarfId: data.bedarfId,
        bewerberId,
        scheduledAt: new Date(data.scheduledAt).toISOString(),
        location: data.location || undefined,
        notes: data.notes || undefined,
        sendEmails: true, // Always send emails when creating from Bewerber context
      });
      toast.success('Kennenlernen erfolgreich geplant und Einladungen versendet');
      reset();
      setSelectedBedarf(null);
      onSuccess?.();
      onClose();
    } catch (err) {
      setError('Fehler beim Erstellen des Kennenlernens');
      toast.error('Fehler beim Erstellen des Kennenlernens');
      console.error('Failed to create kennenlernen:', err);
    }
  };

  const handleClose = () => {
    reset();
    setSelectedBedarf(null);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  const bedarfe = bedarfeData?.data || [];
  // Filter to show only active Bedarfe (ANFRAGE, OFFEN, KENNENLERNGESPRAECH,
  // TERMINIERT or KOSTENTRAEGER_GO)
  const activeBedarfe = bedarfe.filter(
    b => b.status === BedarfStatus.ANFRAGE ||
         b.status === BedarfStatus.OFFEN ||
         b.status === BedarfStatus.KENNENLERNGESPRAECH ||
         b.status === BedarfStatus.TERMINIERT ||
         b.status === BedarfStatus.KOSTENTRAEGER_GO
  );

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative inline-block bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-lg sm:w-full">
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10">
                  <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Kennenlernen planen
                  </h3>
                  <p className="mt-2 text-sm text-gray-500">
                    Planen Sie ein Kennenlernen für <span className="font-medium">{bewerberName}</span>.
                  </p>

                  {error && (
                    <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-sm text-red-600">{error}</p>
                    </div>
                  )}

                  <div className="mt-4 space-y-4">
                    {/* Bedarf Selection */}
                    <div>
                      <label htmlFor="bedarfId" className="block text-sm font-medium text-gray-700">
                        Bedarf auswählen *
                      </label>
                      <select
                        id="bedarfId"
                        {...register('bedarfId', { required: 'Bitte wählen Sie einen Bedarf' })}
                        onChange={(e) => {
                          register('bedarfId').onChange(e);
                          handleBedarfChange(e);
                        }}
                        disabled={isLoadingBedarfe}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:bg-gray-100"
                      >
                        <option value="">
                          {isLoadingBedarfe ? 'Lade Bedarfe...' : '-- Bedarf auswählen --'}
                        </option>
                        {activeBedarfe.map((bedarf) => (
                          <option key={bedarf.id} value={bedarf.id}>
                            {bedarf.childFirstName} {bedarf.childLastName} - {bedarf.schoolName || 'Ohne Schule'}
                          </option>
                        ))}
                      </select>
                      {errors.bedarfId && (
                        <p className="mt-1 text-sm text-red-600">{errors.bedarfId.message}</p>
                      )}
                    </div>

                    {/* Selected Bedarf Info */}
                    {selectedBedarf && (
                      <div className="p-3 bg-gray-50 rounded-md border border-gray-200">
                        <p className="text-sm font-medium text-gray-700">
                          Kind: {selectedBedarf.childFirstName} {selectedBedarf.childLastName}
                        </p>
                        {selectedBedarf.schoolName && (
                          <p className="text-sm text-gray-500">
                            Schule: {selectedBedarf.schoolName}
                          </p>
                        )}
                        {selectedBedarf.parentEmail ? (
                          <p className="text-sm text-green-600">
                            Eltern-E-Mail vorhanden
                          </p>
                        ) : (
                          <p className="text-sm text-yellow-600">
                            Keine Eltern-E-Mail hinterlegt
                          </p>
                        )}
                      </div>
                    )}

                    {/* Date/Time */}
                    <div>
                      <label htmlFor="scheduledAt" className="block text-sm font-medium text-gray-700">
                        Termin *
                      </label>
                      <input
                        type="datetime-local"
                        id="scheduledAt"
                        {...register('scheduledAt', { required: 'Termin ist erforderlich' })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      />
                      {errors.scheduledAt && (
                        <p className="mt-1 text-sm text-red-600">{errors.scheduledAt.message}</p>
                      )}
                    </div>

                    {/* Location */}
                    <div>
                      <label htmlFor="location" className="block text-sm font-medium text-gray-700">
                        Ort
                      </label>
                      <input
                        type="text"
                        id="location"
                        {...register('location')}
                        placeholder="z.B. Schule, Büro..."
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      />
                    </div>

                    {/* Notes */}
                    <div>
                      <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                        Notizen
                      </label>
                      <textarea
                        id="notes"
                        rows={2}
                        {...register('notes')}
                        placeholder="Zusätzliche Informationen..."
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      />
                    </div>

                    <p className="text-xs text-gray-500">
                      Eine Einladung wird automatisch an den Bewerber und die Eltern (falls E-Mail vorhanden) gesendet.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                disabled={createMutation.isPending || isLoadingBedarfe || !watchBedarfId}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
              >
                {createMutation.isPending ? 'Wird erstellt...' : 'Kennenlernen planen'}
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Abbrechen
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
}
